import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    IconButton,
    Tooltip,
    Typography,
    Chip,
    CircularProgress,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import {
    Edit as EditIcon,
    MoreVert as MoreVertIcon,
    AdminPanelSettings as AdminIcon,
    CheckCircle as CheckIcon,
    Cancel as CancelIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';

const UsersTable = ({
    users,
    loading,
    currentUser,
    isSuperAdmin,
    canEdit,
    branches,
    onEditPrivileges,
    onMenuOpen,
    onBranchChange,
    page,
    rowsPerPage,
    total,
    onPageChange,
    onRowsPerPageChange,
    filters,
    isRTL
}) => {
    const { t } = useTranslation();

    const isUserSuperAdmin = (user) => {
        return user?.isSuperAdmin || user?.userType === 'superAdmin';
    };

    const getPrivilegeCount = (user) => user?.privileges?.length || 0;

    const getStatusChip = (user) => {
        if (isUserSuperAdmin(user)) {
            return (
                <Chip
                    icon={<AdminIcon />}
                    label={t('common.superAdmin', 'Super Admin')}
                    color="error"
                    size="small"
                />
            );
        }
        return user.isActive ? (
            <Chip
                icon={<CheckIcon />}
                label={t('common.active', 'Active')}
                color="success"
                size="small"
            />
        ) : (
            <Chip
                icon={<CancelIcon />}
                label={t('common.inactive', 'Inactive')}
                color="default"
                size="small"
            />
        );
    };

    const formatDate = (date) => {
        if (!date) return t('common.never', 'Never');
        return format(new Date(date), 'dd/MM/yyyy');
    };

    const getEmptyMessage = () => {
        if (filters?.status) {
            return t('users.noStatusResults', `No ${filters.status} users found`);
        }
        if (filters?.userType) {
            return t('users.noUserTypeResults', 'No users found with this type');
        }
        if (filters?.branch) {
            return t('users.noBranchResults', 'No users found in this branch');
        }
        if (filters?.search) {
            return t('users.noSearchResults', 'No users found matching your search');
        }
        return t('users.noData', 'No users found');
    };

    return (
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: 'primary.main' }}>
                            <TableCell
                                sx={{
                                    color: 'white',
                                    fontWeight: 'bold',
                                    textAlign: isRTL ? 'right' : 'left'
                                }}
                            >
                                {t('common.user', 'User')}
                            </TableCell>
                            <TableCell
                                sx={{
                                    color: 'white',
                                    fontWeight: 'bold',
                                    textAlign: isRTL ? 'right' : 'left'
                                }}
                            >
                                {t('common.status', 'Status')}
                            </TableCell>
                            <TableCell
                                sx={{
                                    color: 'white',
                                    fontWeight: 'bold',
                                    textAlign: isRTL ? 'right' : 'left'
                                }}
                            >
                                {t('common.privileges', 'Privileges')}
                            </TableCell>
                            <TableCell
                                sx={{
                                    color: 'white',
                                    fontWeight: 'bold',
                                    textAlign: isRTL ? 'right' : 'left'
                                }}
                            >
                                {t('common.lastLogin', 'Last Login')}
                            </TableCell>
                            <TableCell
                                sx={{
                                    color: 'white',
                                    fontWeight: 'bold',
                                    textAlign: isRTL ? 'left' : 'right'
                                }}
                            >
                                {t('common.actions', 'Actions')}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                                    <CircularProgress />
                                </TableCell>
                            </TableRow>
                        ) : users.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                                    <Typography color="textSecondary">
                                        {getEmptyMessage()}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            users.map((user) => (
                                <TableRow
                                    key={user.id || user._id}
                                    hover
                                    sx={{
                                        backgroundColor: !user.isActive
                                            ? 'rgba(244, 67, 54, 0.04)'
                                            : isUserSuperAdmin(user)
                                                ? 'rgba(211, 47, 47, 0.04)'
                                                : 'inherit'
                                    }}
                                >
                                    {/* User Info Cell */}
                                    <TableCell sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                                        <Box>
                                            <Typography variant="subtitle2" fontWeight="bold">
                                                {user.name || user.username}
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary">
                                                @{user.username}
                                            </Typography>
                                            {user.email && (
                                                <Typography variant="body2" color="textSecondary">
                                                    {user.email}
                                                </Typography>
                                            )}

                                            {/* Branch Selection */}
                                            {isSuperAdmin && !isUserSuperAdmin(user) && (
                                                <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                                                    <InputLabel>{t('branches.title', 'Branch')}</InputLabel>
                                                    <Select
                                                        value={user.branch?._id || user.branch || ''}
                                                        label={t('branches.title', 'Branch')}
                                                        onChange={(e) => onBranchChange(user, e.target.value)}
                                                    >
                                                        <MenuItem value="">
                                                            <em>{t('common.none', 'None')}</em>
                                                        </MenuItem>
                                                        {branches.map((branch) => (
                                                            <MenuItem key={branch._id} value={branch._id}>
                                                                {branch.name}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            )}
                                        </Box>
                                    </TableCell>

                                    {/* Status Cell */}
                                    <TableCell sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                                        {getStatusChip(user)}
                                    </TableCell>

                                    {/* Privileges Cell */}
                                    <TableCell sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                                        <Box>
                                            <Typography variant="body2">
                                                {getPrivilegeCount(user)} {t('common.modules', 'modules')}
                                            </Typography>
                                            <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                {user.privileges?.slice(0, 3).map(priv => (
                                                    <Chip
                                                        key={priv.module}
                                                        label={priv.module}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                ))}
                                                {user.privileges?.length > 3 && (
                                                    <Chip
                                                        label={`+${user.privileges.length - 3} ${t('common.more', 'more')}`}
                                                        size="small"
                                                    />
                                                )}
                                            </Box>
                                        </Box>
                                    </TableCell>

                                    {/* Last Login Cell */}
                                    <TableCell sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                                        <Typography variant="body2">
                                            {formatDate(user.lastLogin)}
                                        </Typography>
                                    </TableCell>

                                    {/* Actions Cell */}
                                    <TableCell sx={{ textAlign: isRTL ? 'left' : 'right' }}>
                                        <Box sx={{
                                            display: 'flex',
                                            gap: 1,
                                            justifyContent: isRTL ? 'flex-start' : 'flex-end'
                                        }}>
                                            <Tooltip title={t('common.managePrivileges', 'Manage Privileges')}>
                                                <span>
                                                    <IconButton
                                                        color="primary"
                                                        onClick={() => onEditPrivileges(user)}
                                                        disabled={isUserSuperAdmin(user) || !canEdit}
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>

                                            {isSuperAdmin && !isUserSuperAdmin(user) && (
                                                <Tooltip title={t('common.moreOptions', 'More Options')}>
                                                    <IconButton
                                                        color="default"
                                                        onClick={(e) => onMenuOpen(e, user)}
                                                    >
                                                        <MoreVertIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={total || 0}
                rowsPerPage={rowsPerPage || 10}
                page={page || 0}
                onPageChange={onPageChange}
                onRowsPerPageChange={onRowsPerPageChange}
                labelRowsPerPage={t('common.rowsPerPage', 'Rows per page:')}
                labelDisplayedRows={({ from, to, count }) =>
                    `${from}-${to} ${t('common.of', 'of')} ${count !== -1 ? count : `${t('common.moreThan', 'more than')} ${to}`}`
                }
            />
        </Paper>
    );
};

export default UsersTable;